import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

export class ProgressService extends InjectableClass {
  constructor(
    $state,
    userService,
    progressTypes,
    ViewStatus
  ) {
    'ngInject';
    super({
      $state,
      userService,
      progressTypes,
      ViewStatus
    });

  }

  get activeTypes() {
    return [
      this.progressTypes.ACTIVE,
      this.progressTypes.ACTIVE_AND_PENDING,
      this.progressTypes.ACTIVE_AND_DECLINED,
      this.progressTypes.ACTIVE_AND_COMPLETED
    ];
  }

  get activeInteractionTypes() {
    return [
      this.progressTypes.ACTIVE,
      this.progressTypes.ACTIVE_AND_DECLINED
    ];
  }

  getStatusForCurrentState(routes, ticket) {
    return this.getStatusForState(this.$state.current.name, routes, ticket);
  }

  getStatusForState(state, routes, ticket) {
    let route = _.find(routes, r => r.route === state);

    return route && route.viewStatus && _.findKey(route.viewStatus, (states, viewStatus) => {
      // if view status is completed, look if corresponding status is in ticket history
      if (viewStatus === this.ViewStatus.Completed) {
        // Use history records. Because of some BE bugs, incorrect states
        // were recorded, so we have to exclude them.
        return _.some(ticket.history, h => _.includes(states, h.eventName));
      }

      // otherwise check tickets current status
      return _.includes(states, ticket.status);
    });
  }

  isCurrentInteractable(route, activeRoutes, ticket) {
    const type = this.getTypeForState(this.$state.current.name, route, activeRoutes, ticket);
    return _.includes(this.activeInteractionTypes, type);
  }

  isCompleted(routes, ticket, state) {
    return this.getStatusForState(state, routes, ticket) === this.ViewStatus.Completed;
  }

  isPending(routes, ticket, state) {
    return this.getStatusForState(state, routes, ticket) === this.ViewStatus.Pending;
  }

  isDeclined(routes, ticket, state) {
    return this.getStatusForState(state, routes, ticket) === this.ViewStatus.Declined;
  }

  getStates(routes) {
    return _.map(routes, r => r.route);
  }

  getIsCurrent(state) {
    return this.$state.current.name === state;
  }

  getPreviousState(routes) {
    let index = _.findIndex(routes, r => this.getIsCurrent(r.route));
    if (index > 0) {
      return routes[index - 1].route;
    }
  }

  getNextState(routes) {
    let index = _.findIndex(routes, r => this.getIsCurrent(r.route));
    if (index < routes.length - 1) {
      return routes[index + 1].route;
    }
  }

  getActiveState(routes, activeRoutes, ticket) {
    return activeRoutes && _.find(activeRoutes, ar => {
      const typeForState = this.getTypeForState(ar.route, routes, activeRoutes, ticket);
      return _.includes(this.activeTypes, typeForState) && ar.route !== this.$state.current.name;
    });
  }

  isCurrentActive(routes, activeRoutes, ticket) {
    let current = this.$state.current.name;
    let progressType = this.getTypeForState(current, routes, activeRoutes, ticket);
    return _.includes(this.activeTypes, progressType);
  }

  getTypeForDeadline(state, routes, activeRoutes, ticket) {
    const deadline = this.getDeadlineForState(state, routes);
    const statesWithSameDeadline = _.filter(routes, r => r.deadline && r.deadline.type === deadline.type);
    let types = _.map(statesWithSameDeadline, s => this.getTypeForState(s.route, routes, activeRoutes, ticket));
    types = _.uniq(types);
    return types.length === 1 ? types[0] : this.progressTypes.ACTIVE;
  }

  isDeadlineActive(state, routes, activeRoutes, ticket) {
    const type = this.getTypeForDeadline(state, routes, activeRoutes, ticket);
    return _.includes(this.activeTypes, type);
  }

  getDeadlineForState(state, routes) {
    let route = _.find(routes, r => r.route === state);
    return route && route.deadline;
  }

  getTypeForState(state, routes, activeRoutes, ticket) {
    const stateIndex = _.findIndex(routes, r => r.route === state);
    const currentIndex = _.findIndex(routes, _.first(activeRoutes));
    const currentLastIndex = _.findIndex(routes, _.last(activeRoutes));

    if (stateIndex >= currentIndex && stateIndex <= currentLastIndex) {
      if (this.isPending(routes, ticket, state)) {
        return this.progressTypes.ACTIVE_AND_PENDING;
      }
      if (this.isDeclined(routes, ticket, state)) {
        return this.progressTypes.ACTIVE_AND_DECLINED;
      }
      if (this.isCompleted(routes, ticket, state)) {
        return this.progressTypes.ACTIVE_AND_COMPLETED;
      }

      return this.progressTypes.ACTIVE;
    }
    else if (stateIndex < currentIndex) {
      return this.progressTypes.DONE;
    }
    else {
      return this.progressTypes.UPCOMING;
    }
  }

  /**
   * Check if the given ticket can be navigated to the given state.
   * In case of the user has the revision role, the ticket
   * can always be navigated to every state. Otherwise the ticket
   * can only be navigated to active states.
   */
  mayNavigateTo(state, routes, activeRoutes, ticket) {
    if (this.userService.isRevision()
      || this.$state.params.edit && this.$state.params.edit === 'true') {
      return true;
    }

    const progressType = this.getTypeForState(state, routes, activeRoutes, ticket);
    return _.includes(this.activeTypes, progressType);
  }
}
